// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App, Alert, Button, Input, Space, Tag, Typography } from "antd";
import type { Homeserver } from "../api";
import type { CryptoEngine } from "../crypto";
import type { Session } from "../types";
import { collectLocalKeyring, persistRestoredKeys } from "../keyring";
import { isLocked, unlockedKeyringPrivs } from "../keylock";
import { isBioLocked, unlockedBioPrivs } from "../biolock";
import { setSessionRecoveryKey, hasSessionRecoveryKey } from "../keybackup";

// KeyBackupPanel manages the zero-knowledge encrypted key backup: encrypt this
// browser's device-key keyring under a recovery secret the server never sees, so
// a NEW device can restore it and read old E2E history. The recovery secret is the
// whole security boundary — lose it and the backup is unrecoverable.
export function KeyBackupPanel({
  session,
  engine,
  engineReady,
  hs,
}: {
  readonly session: Session;
  readonly engine: CryptoEngine | null;
  readonly engineReady: boolean;
  readonly hs: Homeserver;
}) {
  const { message } = App.useApp();
  const [hasBackup, setHasBackup] = useState<boolean | null>(null);
  const [secret, setSecret] = useState("");
  const [generated, setGenerated] = useState(""); // a freshly-minted recovery key, shown once
  const [busy, setBusy] = useState(false);
  const [autoOn, setAutoOn] = useState(hasSessionRecoveryKey()); // recovery key held this session?

  useEffect(() => {
    hs.getKeyBackup(session.access_token)
      .then((blob) => setHasBackup(blob !== null))
      .catch(() => setHasBackup(false));
  }, [session.access_token, hs]);

  async function genKey() {
    if (!engine) return;
    const { recovery_key } = await engine.generateRecoveryKey();
    setSecret(recovery_key);
    setGenerated(recovery_key);
  }

  async function createBackup() {
    if (!engine || !secret.trim()) {
      message.warning("Enter or generate a recovery key first");
      return;
    }
    // When keys are encrypted at rest behind a passphrase or the device biometric, the plaintext
    // slots are gone — back up the keyring unlocked into memory this session instead.
    const sessionLocked = isLocked(session.user_id) || isBioLocked(session.user_id);
    let keyring: string[];
    if (isLocked(session.user_id)) keyring = unlockedKeyringPrivs();
    else if (isBioLocked(session.user_id)) keyring = unlockedBioPrivs();
    else keyring = await collectLocalKeyring(session.user_id);
    if (keyring.length === 0) {
      message.warning(
        sessionLocked
          ? "Unlock your device keys this session before backing them up"
          : "No device keys on this browser to back up yet",
      );
      return;
    }
    setBusy(true);
    try {
      const { blob } = await engine.backupKeyring(secret.trim(), JSON.stringify(keyring));
      await hs.putKeyBackup(session.access_token, blob);
      setHasBackup(true);
      setSessionRecoveryKey(secret.trim()); // keep it this session → new keys auto-back-up
      setAutoOn(true);
      message.success(`Backed up ${keyring.length} device key${keyring.length === 1 ? "" : "s"}`);
    } catch (e) {
      message.error(`Backup failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function restoreBackup() {
    if (!engine || !secret.trim()) {
      message.warning("Enter your recovery key to restore");
      return;
    }
    setBusy(true);
    try {
      const blob = await hs.getKeyBackup(session.access_token);
      if (!blob) {
        message.warning("There's no backup to restore");
        return;
      }
      const { keyring } = await engine.restoreKeyring(secret.trim(), blob);
      const privs = JSON.parse(keyring) as string[];
      for (const priv of privs) await engine.addDecryptionKey(priv);
      const added = await persistRestoredKeys(session.user_id, privs);
      setSessionRecoveryKey(secret.trim()); // proven valid → keep it this session for auto-backup
      setAutoOn(true);
      message.success(`Restored ${privs.length} key${privs.length === 1 ? "" : "s"} (${added} new) — reopen rooms to decrypt history`);
      setGenerated("");
    } catch (e) {
      message.error(`Restore failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function deleteBackup() {
    setBusy(true);
    try {
      await hs.deleteKeyBackup(session.access_token);
      setHasBackup(false);
      message.success("Key backup deleted");
    } catch (e) {
      message.error(`Delete failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!engineReady) {
    return <Typography.Text type="secondary">Encryption engine is still starting…</Typography.Text>;
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Typography.Text>
        Status:{" "}
        {hasBackup === null ? <Tag>checking…</Tag> : hasBackup ? <Tag color="green">backup enabled</Tag> : <Tag>not set up</Tag>}
        {hasBackup &&
          (autoOn ? (
            <Tag color="blue">auto-backup on (this session)</Tag>
          ) : (
            <Tag>auto-backup off — restore to enable</Tag>
          ))}
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        A backup lets a new device read old encrypted history. Your device keys are encrypted on this device under a recovery key the
        server never sees — keep it safe; without it the backup can't be recovered.
      </Typography.Paragraph>

      <Space.Compact style={{ width: "100%" }}>
        <Input.Password
          placeholder="Recovery key or passphrase"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          autoComplete="off"
        />
        <Button onClick={genKey} className="kb-generate">Generate</Button>
      </Space.Compact>

      {generated && (
        <Alert
          type="warning"
          showIcon
          message="Save this recovery key now"
          description={<Typography.Text copyable code className="kb-recovery-key">{generated}</Typography.Text>}
        />
      )}

      <Space wrap>
        <Button type="primary" onClick={createBackup} loading={busy} className="kb-create">
          {hasBackup ? "Replace backup" : "Create backup"}
        </Button>
        {hasBackup && (
          <Button onClick={restoreBackup} loading={busy} className="kb-restore">
            Restore on this device
          </Button>
        )}
        {hasBackup && (
          <Button danger onClick={deleteBackup} loading={busy} className="kb-delete">
            Delete backup
          </Button>
        )}
      </Space>
    </Space>
  );
}
